import React, { useCallback, useState, useMemo } from 'react';
import { Select, Tooltip } from 'antd';
import { Flex } from 'antd-v5';
import { ToolOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';

import WorkflowActionNode from './WorkflowActionNode';
import WorkflowHandle from './WorkflowHandle';

import { resolveFormDivisionIds } from '../formHelpers';
import {
  getType,
  getTableFieldColumnsMap,
  selectedFieldsHaveColumn,
  timeEntryTableFieldsHaveColumn,
} from './workflowHelpers';
import HoverHelp from '../../common/HoverHelp';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';

const HOVER_TEXT = (
  <>
    <span>Use this step to update equipment with one or more of the following columns:</span>
    <ul style={{ paddingLeft: 16, margin: 0 }}>
      <li>
        <strong>To Location</strong>
      </li>
      <li>
        <strong>Hours</strong>
      </li>
      <li>
        <strong>Mileage</strong>
      </li>
      <li>
        <strong>Hours Threshold</strong>
      </li>
      <li>
        <strong>Mileage Threshold</strong>
      </li>
    </ul>
  </>
);

const DISABLED_TEXT = (
  'This step requires at least one Equipment table field with one of the following columns: To Location, Hours, Mileage, Hours Threshold, or Mileage Threshold.'
);

const USAGE_SECTION_DISABLED_TEXT = <span>Selected equipment table(s) does not have <strong>Hours</strong> or <strong>Mileage</strong> column. Add one of these columns to update usage.</span>

const THRESHOLD_SECTION_DISABLED_TEXT = <span>Selected equipment table(s) does not have <strong>Hours Threshold</strong> or <strong>Mileage Threshold</strong> column. Add one of these columns to update thresholds.</span>

const CHECK_IN_OUT_SECTION_DISABLED_TEXT = <span>Selected equipment table(s) does not have a <strong>To Location</strong> column. Add this column to update check in/out.</span>

const USAGE_COLUMN_KEYS = new Set(['hours', 'mileage']);
const THRESHOLD_COLUMN_KEYS = new Set(['newHoursThreshold', 'newMileageThreshold']);
const CHECK_IN_OUT_COLUMN_KEYS = new Set(['toProjectId']);

const updateTypeOptions = [
  { value: 'Add', label: 'Add' },
  { value: 'Override', label: 'Override' },
];

export default function WorkflowUpdateEquipmentNode({
  setElements,
  setDataMap,
  isDisplay,
  sections = [],
  divisionId,
  divisionIds = [],
} = {}) {
  const templateDivisionIds = resolveFormDivisionIds({ divisionIds, divisionId });
  return function _({
    id,
    draggable,
    disabled,
    data = {},
  }) {
    const {
      fields: initialSelected = [],
      timeEntryTables: initialTimeEntryTables = [],
      updateType: initialUpdateType = 'Add',
      thresholdUpdateType: initialThresholdUpdateType = 'Add',
      updateUsage: initialUpdateUsage = true,
      updateThreshold: initialUpdateThreshold = true,
      updateCheckInOut: initialUpdateCheckInOut = true,
    } = data;

    const customFields = useSelector((state) => state.timecards?.customFields || {});

    const [selected, setSelected] = useState(initialSelected);
    const [selectedTimeEntryTables, setSelectedTimeEntryTables] = useState(initialTimeEntryTables);
    const [updateType, setUpdateType] = useState(initialUpdateType);
    const [thresholdUpdateType, setThresholdUpdateType] = useState(initialThresholdUpdateType);
    const [updateUsage, setUpdateUsage] = useState(initialUpdateUsage);
    const [updateThreshold, setUpdateThreshold] = useState(initialUpdateThreshold);
    const [updateCheckInOut, setUpdateCheckInOut] = useState(initialUpdateCheckInOut);

    const setters = {
      fields: setSelected,
      timeEntryTables: setSelectedTimeEntryTables,
      updateType: setUpdateType,
      thresholdUpdateType: setThresholdUpdateType,
      updateUsage: setUpdateUsage,
      updateThreshold: setUpdateThreshold,
      updateCheckInOut: setUpdateCheckInOut,
    };

    const onChange = useCallback((key) => (newValue) => {
      setDataMap((dataMap) => {
        const {
          [id]: oldData = {},
        } = dataMap;

        const updatedData = {
          ...oldData,
          [key]: newValue,
        };

        // Handle mutual exclusivity between equipment tables and time entry tables
        if (key === 'fields' && newValue?.length > 0) {
          // When equipment tables are selected, clear time entry tables
          updatedData.timeEntryTables = [];
          setSelectedTimeEntryTables([]);
        } else if (key === 'timeEntryTables' && newValue?.length > 0) {
          // When time entry tables are selected, clear equipment tables
          updatedData.fields = [];
          setSelected([]);
        }

        return {
          ...dataMap,
          [id]: updatedData,
        };
      });
      setters[key](newValue);
    }, [id, setDataMap]);

    const divisionHasEquipmentCustomFields = useMemo(() => (
      templateDivisionIds.some((divId) => {
        if (!customFields[divId]) return false;
        const { fields = [] } = customFields[divId];
        const allSectionFields = fields.flatMap((section) => section.fields);
        return allSectionFields.some((field) => getType(field) === 'table - Equipment');
      })
    ), [templateDivisionIds, customFields]);

    const equipmentTableFields = useMemo(() => (
      sections.reduce((sAcc, section) => {
        if (!section || !section.fields) return sAcc;
        const { fields = [] } = section;
        return sAcc.concat(
          fields.reduce((fAcc, field) => {
            const { id: fieldId, configProps: { title, columns } = {} } = field;
            const type = getType(field);
            if (
              type === 'table - Equipment'
                && columns?.some((col) => col.key === 'toProjectId' || col.key === 'hours' || col.key === 'mileage' || col.key === 'newHoursThreshold' || col.key === 'newMileageThreshold')
            ) fAcc.push({ label: title, value: fieldId });
            return fAcc;
          }, []),
        );
      }, [])
    ), [sections]);

    const timeEntryTableFields = useMemo(() => {
      if (!divisionHasEquipmentCustomFields) return [];
      const allSectionFields = sections.flatMap((section) => section.fields);
      return allSectionFields.filter((field) => {
        const type = getType(field);
        return type === 'table - TimeEntry';
      }).map((field) => {
        const { id: fieldId, configProps: { title } = {} } = field;
        return { label: title, value: fieldId };
      });
    }, [sections, divisionHasEquipmentCustomFields]);

    const {
      equipmentHasUsage,
      equipmentHasThreshold,
      equipmentHasCheckInOut,
    } = useMemo(() => {
      const map = getTableFieldColumnsMap({ sections, tableFieldType: 'table - Equipment' });
      return {
        tableFieldIdToColumns: map,
        equipmentHasUsage: selectedFieldsHaveColumn({
          tableFieldIdToColumns: map,
          tableFieldIds: selected,
          columnKeys: USAGE_COLUMN_KEYS,
        }),
        equipmentHasThreshold: selectedFieldsHaveColumn({
          tableFieldIdToColumns: map,
          tableFieldIds: selected,
          columnKeys: THRESHOLD_COLUMN_KEYS,
        }),
        equipmentHasCheckInOut: selectedFieldsHaveColumn({
          tableFieldIdToColumns: map,
          tableFieldIds: selected,
          columnKeys: CHECK_IN_OUT_COLUMN_KEYS,
        }),
      };
    }, [sections, selected]);

    const {
      timeEntryHasUsage,
      timeEntryHasThreshold,
      timeEntryHasCheckInOut,
    } = useMemo(() => {
      const mergedCustomFieldSections = templateDivisionIds.flatMap(
        (divId) => customFields[divId]?.fields ?? [],
      );
      const map = getTableFieldColumnsMap({
        sections: mergedCustomFieldSections,
        tableFieldType: 'table - Equipment',
      });
      return {
        timeEntryTableFieldIdToColumns: map,
        timeEntryHasUsage: timeEntryTableFieldsHaveColumn({
          tableFieldIdToColumns: map,
          columnKeys: USAGE_COLUMN_KEYS,
        }),
        timeEntryHasThreshold: timeEntryTableFieldsHaveColumn({
          tableFieldIdToColumns: map,
          columnKeys: THRESHOLD_COLUMN_KEYS,
        }),
        timeEntryHasCheckInOut: timeEntryTableFieldsHaveColumn({
          tableFieldIdToColumns: map,
          columnKeys: CHECK_IN_OUT_COLUMN_KEYS,
        }),
      };
    }, [customFields, templateDivisionIds]);

    const hasTimeEntryTables = selectedTimeEntryTables?.length > 0;
    const hasEquipmentTables = selected?.length > 0;

    const isUsageUpdateEnabled = (hasEquipmentTables || hasTimeEntryTables)
      && (hasTimeEntryTables ? timeEntryHasUsage : equipmentHasUsage);

    const isThresholdUpdateEnabled = (hasEquipmentTables || hasTimeEntryTables)
      && (hasTimeEntryTables ? timeEntryHasThreshold : equipmentHasThreshold);

    const isCheckInOutUpdateEnabled = (hasEquipmentTables || hasTimeEntryTables)
      && (hasTimeEntryTables ? timeEntryHasCheckInOut : equipmentHasCheckInOut);

    const title = `Update Equipment${disabled ? ' - DISABLED' : ''}`;

    return (
      <WorkflowActionNode
        id={id}
        title={title}
        Icon={ToolOutlined}
        type="updateEquipment"
        draggable={draggable}
        onNodeUpdate={setElements}
        isDisplay={isDisplay || disabled}
        hover={disabled ? DISABLED_TEXT : HOVER_TEXT}
        style={disabled ? { opacity: 0.7 } : {}}
      >
        {!draggable && (
        <Flex vertical gap={20} style={{ margin: '10px 0px' }}>
          <Flex vertical>
            <span>Select Equipment Table Field(s)</span>
            <Select
              mode="multiple"
              onChange={onChange('fields')}
              value={selected}
              options={equipmentTableFields}
              optionFilterProp="label"
              disabled={selectedTimeEntryTables?.length > 0}
            />
          </Flex>
          {timeEntryTableFields.length > 0 && (
            <Flex vertical>
              <Flex gap={5} align="center">
                <span>Select Time Entry Table Field(s)</span>
                <HoverHelp
                  placement="topRight"
                  content={(
                    <div style={{ width: 300 }}>
                      The equipment data from the selected time entry
                      {' '}tables will be used to update equipment.
                    </div>
                  )}
                />
              </Flex>
              <Select
                mode="multiple"
                onChange={onChange('timeEntryTables')}
                value={selectedTimeEntryTables}
                options={timeEntryTableFields}
                optionFilterProp="label"
                disabled={selected?.length > 0}
              />
            </Flex>
          )}
          <Flex vertical style={{ opacity: isCheckInOutUpdateEnabled ? 1 : 0.6 }}>
            <Tooltip title={isCheckInOutUpdateEnabled ? null : CHECK_IN_OUT_SECTION_DISABLED_TEXT} placement="topLeft">
              <OnTraccrCheckbox
                label="Check In/Out?"
                value={updateCheckInOut}
                onChange={onChange('updateCheckInOut')}
                disabled={!isCheckInOutUpdateEnabled}
                hoverNode={<div style={{ width: 300 }}>
                  If turned on, the equipment will be checked in/out based on the <strong>To Location</strong> column.
                </div>}
              />
            </Tooltip>
          </Flex>
          <Flex vertical style={{ opacity: isUsageUpdateEnabled ? 1 : 0.6 }}>
            <Tooltip title={isUsageUpdateEnabled ? null : USAGE_SECTION_DISABLED_TEXT} placement="topLeft">
              <OnTraccrCheckbox
                label="Update Usage?"
                value={updateUsage}
                onChange={onChange('updateUsage')}
                style={{ marginBottom: 5 }}
                disabled={!isUsageUpdateEnabled}
              />
            </Tooltip>
            <Flex gap={5} align="center">
              <span>Usage Update Type</span>
              <HoverHelp
                placement="topRight"
                content={(
                  <div style={{ width: 300 }}>
                    <div style={{ fontWeight: 'bold', marginBottom: 5 }}>Add:</div>
                    <div>
                      Hours/Mileage value is added from the Hours/Mileage column
                    </div>
                    <div style={{ fontWeight: 'bold', marginBottom: 5, marginTop: 10 }}>Override:</div>
                    <div>
                      Hours/Mileage value is overridden from the Hours/Mileage column
                    </div>
                  </div>
                )}
              />
            </Flex>
            <Select
              onChange={onChange('updateType')}
              value={updateType}
              options={updateTypeOptions}
              optionFilterProp="label"
              disabled={!updateUsage || !isUsageUpdateEnabled}
            />
          </Flex>
            <Flex vertical style={{ opacity: isThresholdUpdateEnabled ? 1 : 0.6 }}>
            <Tooltip title={isThresholdUpdateEnabled ? null : THRESHOLD_SECTION_DISABLED_TEXT} placement="topLeft">
              <OnTraccrCheckbox
                label="Update Threshold?"
                value={updateThreshold}
                onChange={onChange('updateThreshold')}
                style={{ marginBottom: 5 }}
                disabled={!isThresholdUpdateEnabled}
              />
            </Tooltip>
            <Flex gap={5} align="center">
              <span>Threshold Update Type</span>
              <HoverHelp
                placement="topRight"
                content={(
                  <div style={{ width: 300 }}>
                    <div style={{ fontWeight: 'bold', marginBottom: 5 }}>Add:</div>
                    <div>
                      Threshold value is added from the Hours/Mileage Threshold column
                    </div>
                    <div style={{ fontWeight: 'bold', marginBottom: 5, marginTop: 10 }}>Override:</div>
                    <div>
                      Threshold value is overridden from the Hours/Mileage Threshold column
                    </div>
                  </div>
                )}
              />
            </Flex>
            <Select
              onChange={onChange('thresholdUpdateType')}
              value={thresholdUpdateType}
              options={updateTypeOptions}
              optionFilterProp="label"
              disabled={!updateThreshold || !isThresholdUpdateEnabled}
            />
          </Flex>
          <WorkflowHandle type="target" position="top" disabled={isDisplay} />
        </Flex>
        )}
      </WorkflowActionNode>
    );
  };
}
